/** @jsx jsx */
import { jsx } from "theme-ui"
import React, { useCallback } from "react"
import {
  GoogleMapProps as ReactGoogleMapsProps,
  GoogleMap as ReactGoogleMap,
} from "@react-google-maps/api"
import { PrimitiveProps } from "@styles/types"
import { Box } from "@shared-components/Box/Box"
import { useGoogleMapInstanceContext } from "../../../context/googleMapInstance/useGoogleMapInstanceProvider"
import { ImageField } from "../../../carlsberg-headless/types/fields/Image"
import { UmbracoBackgroundImage } from "@shared-components/Image/UmbracoBackgroundImage/UmbracoBackgroundImage"

export interface GoogleMapsProps extends PrimitiveProps, ReactGoogleMapsProps {
  fallbackImage?: ImageField
  fallbackImageWidth?: number[]
}

export const googleMapsStyles: React.CSSProperties = {
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  width: "100%",
  height: "100%",
}

const googleMapsStaticOptions = {
  disableDefaultUI: true,
}

const MapInstance: React.FC<Omit<GoogleMapsProps, "googleMapsApiKey">> = ({
  children,
  options,
  ...props
}) => {
  const mapInstance = React.useRef<google.maps.Map | null>(null)
  const onLoad = useCallback(function onLoad(_mapInstance: google.maps.Map) {
    mapInstance.current = _mapInstance
  }, [])

  return (
    <ReactGoogleMap
      options={{
        ...googleMapsStaticOptions,
        ...options,
      }}
      {...props}
      mapContainerStyle={googleMapsStyles}
      onLoad={onLoad}
    >
      {children}
    </ReactGoogleMap>
  )
}

const GoogleMap: React.FC<GoogleMapsProps> = ({
  sx,
  fallbackImage,
  fallbackImageWidth,
  ...props
}) => {
  const { isLoaded, loadError } = useGoogleMapInstanceContext()
  if (loadError) {
    return fallbackImage ? (
      <UmbracoBackgroundImage
        image={fallbackImage}
        width={fallbackImageWidth}
      />
    ) : (
      <Box>Map cannot be loaded right now, sorry.</Box>
    )
  }

  return isLoaded ? (
    <Box sx={{ ...sx }}>
      <MapInstance {...props} />
    </Box>
  ) : null
}

GoogleMap.defaultProps = {}

export { GoogleMap }
